// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga bolts.cpp Bolt_HitGameObjectRC, bolt.cpp FindIncomingBolt,
// pushblocks.cpp NewBlockAction and move.cpp BlockCode/HoldCode.
import { sourceAtan2, sourceBoltAnglesDirection } from './projectiles.mjs';

const vec = value => Array.isArray(value) && value.length === 3 && value.every(Number.isFinite);
const contextFlags = { none: 0x1000, block: 0x04000010, hold: 0x04000010, combo: 0x00800010,
  swipe: 0x00800010, jump: 0x01000000, bigjump: 0x02400111 };
const distance2 = (a, b) => a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0);
function qrand(random) { const value = random(); if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error('Invalid original deflection random source'); return Math.min(65535, Math.floor(value * 65536)); }

export function findSourceIncomingBolt(bolts, collision, { excludePlayers = false } = {}) {
  if (!collision || !vec(collision.position) || !Number.isFinite(collision.radius) || !Number.isFinite(collision.halfHeight))
    throw new Error('Original incoming-bolt collision volume required');
  const radius = Math.max(collision.radius, collision.halfHeight);
  let nearest = null, nearestDistance = 1e8;
  for (const bolt of bolts) {
    if (!bolt.active || (excludePlayers && bolt.owner?.playerControlled)) continue;
    const delta = collision.position.map((v, i) => v - bolt.position[i]);
    const distance = distance2(bolt.position, collision.position), projection = delta.reduce((sum, v, i) => sum + v * bolt.direction[i], 0);
    // Original desktop horizon=.5s. Android touch-specific1.5s is excluded.
    if (distance < (.5 * bolt.speed) ** 2 && projection >= 0 && distance - projection ** 2 <= radius ** 2 && distance < nearestDistance) {
      nearest = bolt; nearestDistance = distance;
    }
  }
  return nearest;
}

export function selectSourceBlockAnimation(actor, previous, random = Math.random) {
  const actions = ['block1', 'block2', 'block3'].map(name => actor.config?.actions?.get(name))
    .filter(action => action && actor.animations?.has(action.clip));
  if (!actions.length) return null;
  if (actions.length === 1) return actions[0].clip;
  // Native qrand/(0xffff/count+1) chooses uniformly, retrying previous action.
  for (let attempts = 0; attempts < 64; attempts++) {
    const index = Math.min(actions.length - 1, Math.floor(qrand(random) / (Math.floor(65535 / actions.length) + 1)));
    if (actions[index].clip !== previous) return actions[index].clip;
  }
  throw new Error('Original block animation random source repeats previous action');
}

// Bolts may be harmless only in an actual native protection context. A raw
// held key is not a context and does not grant invulnerability. Caller owns
// input mapping and entry into Block/Hold/Combo/Jump; unknown contexts report
// failure and allow the ordinary damage path to run.
export function createSourceDeflector({ getContext, random = Math.random, onBlockAnimation = () => {} } = {}) {
  if (typeof getContext !== 'function') throw new Error('Original character context adapter required');
  const report = { blocked: [], limitations: ['Touch-control deflection, cheats and bonus timers require explicit caller context.',
    'Player-owned intercept prediction requires caller-provided ownerInterceptDirection; ordinary enemy bolts use their incoming path.',
    'Full melee block contexts, hold entry permission and non-blaster projectile types remain caller responsibilities.'] };
  return { report, interceptCharacterHit(actor, amount, { bolt, point }) {
    const context = getContext(actor, bolt);
    if (!context || typeof context.playerControlled !== 'boolean' || typeof context.jedi !== 'boolean' ||
        !vec(point) || !vec(bolt.previousPosition)) {
      report.blocked.push({ actor: actor.id, reason: 'Unknown original bolt-defense context' }); return { handled: false };
    }
    const name = String(context.name ?? '').toLowerCase();
    const flags = Number.isInteger(context.flags) ? context.flags : contextFlags[name];
    if (flags === undefined) { report.blocked.push({ actor: actor.id, reason: `Unknown original context ${name}` }); return { handled: false }; }
    const deflect = context.playerControlled && context.hasDeflectBolts === true;
    const jumpFlags = context.jumpFlags ?? 0, boltFlags = bolt.flags ?? 0;
    const protectedContext = !!(flags & 0x4000000) || !!((flags & 0x8000000) && (jumpFlags & 2)) ||
      !!(flags & 0x800000) || !!((flags & 0x1000000) && (jumpFlags & 1)) || context.cannotKill === true || deflect;
    if ((boltFlags & 0x100) && !deflect || !protectedContext) return { handled: false };
    if (name === 'hold') {
      const clip = selectSourceBlockAnimation(actor, actor.previousBlockAnimation, random);
      if (clip) { actor.previousBlockAnimation = clip; onBlockAnimation(actor, clip); }
    }
    if (name !== 'block' && !deflect && bolt.time < .1) return { handled: true, mode: 'absorb' };
    let direction;
    if (context.ownerRequiresIntercept === true) {
      direction = context.ownerInterceptDirection;
      if (!vec(direction)) { report.blocked.push({ actor: actor.id, reason: 'Original player-owner intercept prediction unavailable' }); return { handled: false }; }
    } else direction = bolt.previousPosition.map((v, i) => v - point[i]);
    if (!direction.some(v => v !== 0)) { report.blocked.push({ actor: actor.id, reason: 'Degenerate original reflected direction' }); return { handled: false }; }
    let yaw = sourceAtan2(direction[0], direction[2]);
    let pitch = -sourceAtan2(direction[1], Math.sqrt(direction[0] ** 2 + direction[2] ** 2));
    const perfect = (!(boltFlags & 0x40) || deflect) && context.playerControlled &&
      (name === 'block' || context.cheatDeflect === true) && context.jedi && context.ownerReturnEligible !== false;
    if (!perfect) {
      yaw += Math.trunc(qrand(random) / 65535 * 16384 - 8192);
      pitch += Math.trunc(qrand(random) / 65535 * 16384 - 5461);
      pitch = Math.max(-8192, Math.min(8192, pitch));
    }
    return { handled: true, mode: 'reflect', perfect, reflected: { owner: actor, position: point.slice(),
      direction: sourceBoltAnglesDirection(yaw & 65535, pitch & 65535),
      flags: 0x10000000 | (context.playerControlled ? 0x28 | (context.playerIndex === 1 ? 2 : 1) : 0) } };
  } };
}

// Desktop bolt-only BlockCode subset. Hold may be entered only after the
// caller confirms native CanStartHold permission. There is no invented
// perpetual key-held immunity. Block's incoming-bolt latch=.3s, release=.2s;
// Hold retains its initial .3s before a released action can end it.
export function createSourceBlockController(actor, { random = Math.random } = {}) {
  const state = { name: 'none', clip: null, remaining: 0, latch: false, cooldown: 0 };
  const choose = () => { const clip = selectSourceBlockAnimation(actor, actor.previousBlockAnimation, random); if (!clip) return false; state.clip = clip; actor.previousBlockAnimation = clip; return true; };
  return { state, tick(dt, { pressed = false, held = false, grounded, weaponDrawn, allowStart = true,
    contextForbidden = false, bolts = [], collision } = {}) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid original block timestep');
    const incoming = collision ? findSourceIncomingBolt(bolts, collision) : null;
    if (state.name === 'hold') { if (state.remaining > 0) state.remaining -= dt; else if (!held) { state.name = 'none'; state.clip = null; } return state; }
    if (state.name === 'block') {
      if (!state.latch) { state.remaining -= dt; if (state.remaining <= 0) { state.latch = true; state.remaining = .2; } return state; }
      if (pressed && incoming && choose()) { state.remaining = .3; state.latch = false; return state; }
      if (held && incoming) { state.remaining = .2; return state; }
      state.remaining -= dt;
      if (state.remaining <= 0) { state.name = 'none'; state.clip = null; }
      return state;
    }
    if (state.cooldown > 0) { state.cooldown = Math.max(0, state.cooldown - dt); return state; }
    if (allowStart && pressed && grounded === true && weaponDrawn === true && !contextForbidden && incoming && choose()) {
      state.name = 'block'; state.remaining = .3; state.latch = false;
    }
    return state;
  }, beginHold({ permitted } = {}) {
    if (permitted !== true || !choose()) return false;
    state.name = 'hold'; state.remaining = .3; state.latch = false; return true;
  }, reset() { Object.assign(state, { name: 'none', clip: null, remaining: 0, latch: false, cooldown: 0 }); } };
}

// DrawWeapons chooses ObjTab[0xd]="blaster" when weapon_model=-1 and
// (model_flags&0x90)==0x80. BattleDroid's blaster keyword supplies0x80 and
// no explicit weapon model; its droid keyword does not set the excluded0x10.
export async function attachThingsBlaster(character, things) {
  if (character.config?.values?.variant !== 'battledroid' || !character.config.values.blaster || character.config.values.weapon !== undefined)
    throw new Error('Unsupported original generic blaster selection');
  const THREE = await import('three'), { DDSLoader } = await import('three/addons/loaders/DDSLoader.js');
  const { parseGSC, decodeMesh } = await import('./gsc.mjs'), { evaluatePointOfInterest } = await import('./ghg.mjs');
  const parsed = things?.specialObjects ? things : parseGSC(things);
  const special = parsed.specialObjects.find(value => value.name.toLowerCase() === 'blaster');
  if (!special?.parts.length || special.parts.some(part => part.type === 'faceon')) throw new Error('Missing/unsupported original THINGS blaster geometry');
  const locator = character.config.values.weapon_locator ?? character.config.values.weapon_locator_1;
  if (!Number.isInteger(locator) || !evaluatePointOfInterest(character.parsed, locator, character.matrices)) throw new Error('Missing original blaster weapon locator');
  const root = new THREE.Group(), geometries = [], materials = [], textures = new Map(), dds = new DDSLoader();
  root.matrixAutoUpdate = false; root.name = 'original-blaster';
  for (const part of special.parts) {
    const decoded = decodeMesh(parsed, part.mesh, part.material), source = part.material;
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(decoded.positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(decoded.uvs, 2));
    geometry.setAttribute('color', new THREE.BufferAttribute(decoded.colors, 3));
    geometry.setIndex(new THREE.BufferAttribute(decoded.indices, 1));
    if (decoded.hasNormals) geometry.setAttribute('normal', new THREE.BufferAttribute(decoded.normals, 3)); else geometry.computeVertexNormals();
    let map = textures.get(source.texture) ?? null;
    if (!map && parsed.textures.has(source.texture)) {
      const decodedTexture = dds.parse(parsed.textures.get(source.texture).data, true);
      if (!decodedTexture.format || decodedTexture.isCubemap) throw new Error('Unsupported original blaster DDS texture');
      map = decodedTexture.format === THREE.RGBAFormat ? new THREE.DataTexture(decodedTexture.mipmaps[0].data, decodedTexture.width, decodedTexture.height, THREE.RGBAFormat)
        : new THREE.CompressedTexture(decodedTexture.mipmaps, decodedTexture.width, decodedTexture.height, decodedTexture.format);
      map.flipY = false; map.colorSpace = THREE.SRGBColorSpace; map.needsUpdate = true; textures.set(source.texture, map);
    }
    const material = new THREE.MeshLambertMaterial({ map, color: map ? 0xffffff : new THREE.Color(...source.color.slice(0, 3)), vertexColors: decoded.hasColors });
    const mesh = new THREE.Mesh(geometry, material); mesh.frustumCulled = false; root.add(mesh);
    geometries.push(geometry); materials.push(material);
  }
  character.model.add(root);
  const update = (visible = true) => {
    const matrix = evaluatePointOfInterest(character.parsed, locator, character.matrices);
    root.visible = visible && !!matrix; if (matrix) root.matrix.fromArray(matrix);
  };
  update();
  return { root, update, summary: { model: 'blaster', originalObjectId: 13, meshes: geometries.length, locator },
    dispose() { root.removeFromParent(); geometries.forEach(value => value.dispose()); materials.forEach(value => value.dispose()); textures.forEach(value => value.dispose()); } };
}
